/** Add a manual order (the old "Stock out"): customer + stock products → deducts stock (or queues for manager approval) and lands in To pack. */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, PackagePlus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { OUT_CATEGORIES, PAYMENT_METHODS, packApi } from './shared';

const Field = ({ label, children, className = '' }) => (
  <div className={className}><Label className="text-xs font-semibold text-gray-600">{label}</Label><div className="mt-1">{children}</div></div>
);
const Sel = (props) => <select {...props} className="w-full h-10 border rounded-md px-3 text-sm bg-white" />;

export function ManualOrderDialog({ onClose, onDone }) {
  const [products, setProducts] = useState(null);
  const [form, setForm] = useState({ customer_name: '', phone: '', email: '', address: '', delivery_notes: '', out_category: '', sale_value: '', payment_method: '', invoice_number: '', notes: '', priority: 'no_rush' });
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { packApi('GET', '/stock-products').then(setProducts).catch((e) => toast.error(e.message)); }, []);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const byCat = new Map();
    for (const p of products || []) {
      if (q && !p.name.toLowerCase().includes(q) && !(p.category || '').toLowerCase().includes(q)) continue;
      const c = p.category || 'Uncategorised';
      if (!byCat.has(c)) byCat.set(c, []);
      byCat.get(c).push(p);
    }
    return Array.from(byCat.entries());
  }, [products, filter]);

  const toggle = (p) => {
    if ((p.qty_on_hand || 0) <= 0) { toast.error(`${p.name} has 0 on hand`); return; }
    setSelected((s) => { const n = { ...s }; if (p.id in n) delete n[p.id]; else n[p.id] = '1'; return n; });
  };
  const count = Object.keys(selected).length;

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!form.out_category) { toast.error('Pick a category'); return; }
    if (!form.payment_method) { toast.error('Pick a payment method'); return; }
    const v = parseFloat(form.sale_value);
    if (!Number.isFinite(v) || v < 0) { toast.error('Sale value is required'); return; }
    const items = Object.entries(selected).map(([product_id, q]) => ({ product_id, qty: parseInt(q, 10) })).filter((i) => i.qty > 0);
    if (!items.length) { toast.error('Tick at least one product'); return; }
    for (const it of items) {
      const p = products.find((pp) => pp.id === it.product_id);
      if (p && it.qty > (p.qty_on_hand || 0)) { toast.error(`Only ${p.qty_on_hand} × ${p.name} on hand`); return; }
    }
    setBusy(true);
    try {
      const d = await packApi('POST', '/manual', { ...form, sale_value: v, items });
      if (d.pending_approval) toast.success(`${d.order.reference} sent to your manager for approval`, { description: 'Stock will be deducted and the order will be ready to pack once approved.' });
      else toast.success(`${d.order.reference} added to To pack`, { description: `${items.length} line${items.length === 1 ? '' : 's'} deducted from stock` });
      onDone(d.order);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col" data-testid="manual-order-dialog">
      <div className="text-white px-4 py-3 flex items-center gap-3 shrink-0" style={{ background: '#b45309' }}>
        <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/10" aria-label="Close" data-testid="manual-order-close"><X className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Manual order · stock out</div>
          <div className="font-bold text-lg leading-tight">Add an order to pack</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 max-w-2xl w-full mx-auto">
        <section className="bg-white rounded-xl border p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Customer / company *" className="sm:col-span-2"><Input value={form.customer_name} onChange={set('customer_name')} placeholder="e.g. Smith Driveways Ltd" data-testid="manual-customer" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={set('phone')} data-testid="manual-phone" /></Field>
          <Field label="Email"><Input value={form.email} onChange={set('email')} data-testid="manual-email" /></Field>
          <Field label="Delivery address" className="sm:col-span-2"><Textarea value={form.address} onChange={set('address')} rows={2} className="bg-white" data-testid="manual-address" /></Field>
          <Field label="Delivery notes" className="sm:col-span-2"><Input value={form.delivery_notes} onChange={set('delivery_notes')} placeholder="e.g. leave with neighbour" data-testid="manual-delivery-notes" /></Field>
        </section>

        <section className="bg-white rounded-xl border p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Category *"><Sel value={form.out_category} onChange={set('out_category')} data-testid="manual-category"><option value="">— select —</option>{OUT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</Sel></Field>
          <Field label="Priority"><Sel value={form.priority} onChange={set('priority')} data-testid="manual-priority"><option value="no_rush">No rush</option><option value="soon">Soon</option><option value="urgent">Urgent</option></Sel></Field>
          <Field label="Total sale value (£) *"><Input type="number" min="0" step="0.01" value={form.sale_value} onChange={set('sale_value')} placeholder="0.00" data-testid="manual-value" /></Field>
          <Field label="Payment method *"><Sel value={form.payment_method} onChange={set('payment_method')} data-testid="manual-method"><option value="">— select —</option>{PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</Sel></Field>
          <Field label="Invoice number"><Input value={form.invoice_number} onChange={set('invoice_number')} placeholder="INV-12345" data-testid="manual-invoice" /></Field>
          <Field label="Internal notes"><Input value={form.notes} onChange={set('notes')} data-testid="manual-notes" /></Field>
        </section>

        <section>
          <div className="flex items-center justify-between mb-1.5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">Products going out</h2>
            <span className="text-xs font-bold text-gray-700" data-testid="manual-selected-count">{count} selected</span>
          </div>
          <div className="relative mb-2">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a product…" className="w-full h-10 pl-9 pr-3 rounded-xl border bg-white text-sm" data-testid="manual-filter" />
          </div>
          {!products && <div className="text-center text-gray-400 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {products && grouped.length === 0 && <div className="bg-white rounded-xl border p-6 text-center text-sm text-gray-500 italic">No products match.</div>}
          <div className="space-y-2">
            {grouped.map(([cat, list]) => (
              <div key={cat} className="bg-white rounded-xl border overflow-hidden" data-testid={`manual-cat-${cat}`}>
                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-gray-600 bg-gray-100">{cat}</div>
                <ul className="divide-y">
                  {list.map((p) => {
                    const on = p.id in selected;
                    const out = (p.qty_on_hand || 0) <= 0;
                    return (
                      <li key={p.id} className={`flex items-center gap-3 px-3 py-2 ${out ? 'opacity-50' : ''} ${on ? 'bg-amber-50' : ''}`} data-testid={`manual-product-${p.id}`}>
                        <input type="checkbox" checked={on} disabled={out} onChange={() => toggle(p)} className="w-5 h-5 accent-amber-600 shrink-0" data-testid={`manual-check-${p.id}`} />
                        <button type="button" onClick={() => !out && toggle(p)} className="flex-1 min-w-0 text-left">
                          <div className="text-sm text-gray-900 truncate">{p.name}</div>
                          <div className={`text-[11px] ${out ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>{p.qty_on_hand} on hand{out ? ' — none to send' : ''}</div>
                        </button>
                        {on && <Input type="number" min="1" max={p.qty_on_hand} value={selected[p.id]} onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.value }))} aria-label={`Quantity for ${p.name}`} className="w-20 shrink-0 text-right" data-testid={`manual-qty-${p.id}`} />}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="shrink-0 border-t bg-white px-4 py-3 max-w-2xl w-full mx-auto">
        <button type="button" onClick={submit} disabled={busy || !count} className="w-full h-14 rounded-xl bg-amber-600 disabled:bg-gray-300 disabled:text-gray-500 text-white font-bold text-base inline-flex items-center justify-center gap-2" data-testid="manual-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackagePlus className="w-5 h-5" />} {count ? `Add order · deduct ${count} line${count === 1 ? '' : 's'} from stock` : 'Tick the products going out'}
        </button>
      </div>
    </div>
  );
}
